import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { useCountdown, getVaultLaunchDate } from '../hooks/useCountdown'

/**
 * BTC Rate Display Component for Bitcoin Vault
 */
function BtcRateCompact() {
  const { t } = useTranslation(['vaults'])
  const { data, isLoading } = useQuery({
    queryKey: ['btcPrice'],
    queryFn: () => api.getBtcPrice(),
    refetchInterval: 60000,
    retry: 2,
    staleTime: 30000,
  })

  if (isLoading || !data?.data) {
    return null
  }

  const price = data.data.price
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)

  return (
    <div className="mt-2 text-xs text-orange-300/80">
      {t('vaults:bitcoin.current')}: <span className="font-semibold text-orange-400">{formattedPrice}</span>
    </div>
  )
}
import { QUERY_CONFIG } from '../hooks/useQueries'

export default function VaultsPage() {
  const { t } = useTranslation(['vaults', 'common'])

  // Fetch all active vaults (no filtering in v3.0)
  const { data: vaultsResponse, isLoading, error } = useQuery({
    queryKey: ['vaults'],
    queryFn: () =>
      api.getVaults({
        isActive: true, // Only show active vaults for new investments
      }),
    ...QUERY_CONFIG.vaults,
  })

  // All vaults - no filtering needed in v3.0
  const vaults = vaultsResponse?.data || []
  
  // Get vault launch date from environment variable (must include timezone offset)
  // Memoize to prevent re-parsing on every render
  const launchDate = useMemo(() => getVaultLaunchDate(), [])
  const countdown = useCountdown(launchDate)
  // Vault purchasing is enabled if:
  // - No launch date is set (no countdown required), OR
  // - Launch date is set AND countdown has expired
  const isVaultPurchasingEnabled = launchDate === null || countdown.isExpired

  return (
    <div className="min-h-screen py-6 sm:py-12">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center space-y-3 sm:space-y-4 mb-6 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white">
            {t('vaults:page.title')}
          </h1>
          <p className="text-sm sm:text-lg text-gray-400 max-w-2xl mx-auto px-2">
            {t('vaults:page.subtitle')}
          </p>
        </div>

        {/* Vaults Grid */}
        {isLoading ? (
          <div className="text-center py-20">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-400">{t('vaults:page.loadingVaults')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-400">{t('vaults:page.errorLoading')}</p>
          </div>
        ) : vaults.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400">{t('vaults:page.noVaults')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {vaults.map((vault) => (
              <div
                key={vault.id}
                className={`relative bg-background-card rounded-xl p-4 sm:p-6 border transition-all ${
                  vault.isComingSoon
                    ? 'border-orange-500/30 opacity-75'
                    : 'border-green-900/20 card-glow hover:border-gold-500/50'
                }`}
              >
                {/* Coming Soon Badge */}
                {vault.isComingSoon && (
                  <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg z-10">
                    {t('vaults:comingSoon')}
                  </div>
                )}

                {/* Bitcoin Vault - Simplified Display */}
                {vault.isComingSoon ? (
                  <>
                    {/* Vault Name */}
                    <h3 className="text-xl font-bold text-white mb-4">{vault.name}</h3>

                    {/* TAKARA Yields → Bitcoin Rewards */}
                    <div className="flex-1 flex flex-col justify-center items-center py-8">
                      <div className="text-center space-y-6">
                        {/* TAKARA Yield */}
                        <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-xl">
                          <div className="text-3xl mb-2">🪙</div>
                          <div className="text-lg font-bold text-green-400">{t('vaults:yield.takaraYield')}</div>
                          <div className="text-sm text-gray-400 mt-1">{t('vaults:yield.stakeYourTakara')}</div>
                        </div>

                        {/* Arrow */}
                        <div className="text-3xl text-orange-400">↓</div>

                        {/* Bitcoin Earnings */}
                        <div className="p-4 bg-orange-900/20 border border-orange-500/30 rounded-xl">
                          <div className="text-3xl mb-2">₿</div>
                          <div className="text-lg font-bold text-orange-400">{t('vaults:yield.bitcoinEarnings')}</div>
                          <div className="text-sm text-gray-400 mt-1">{t('vaults:yield.earnBTC')}</div>
                          <BtcRateCompact />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Vault Name */}
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">{vault.name}</h3>

                    {/* Duration */}
                    <p className="text-sm sm:text-base text-gray-400 mb-3 sm:mb-4">{vault.duration} {t('vaults:card.lockup')}</p>

                    {/* Stats */}
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      <div className="flex justify-between items-center">
                        <span className="text-xs sm:text-sm text-gray-400">{t('vaults:card.baseAPY')}</span>
                        <span className="text-base sm:text-lg font-semibold text-gold-500">
                          {vault.baseAPY}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs sm:text-sm text-gray-400">{t('vaults:card.maxAPY')}</span>
                        <span className="text-base sm:text-lg font-semibold text-gradient-gold">
                          {vault.maxAPY}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center bg-gold-500/10 -mx-2 px-2 py-1.5 sm:py-2 rounded">
                        <span className="text-xs sm:text-sm text-gray-400">{t('vaults:card.totalReturn')}</span>
                        <span className="text-sm sm:text-lg font-bold text-gold-400">
                          {(vault.baseAPY * vault.duration / 12).toFixed(1)}% → {(vault.maxAPY * vault.duration / 12).toFixed(1)}%
                        </span>
                      </div>
                      {/* TAKARA APY - Dynamic from TVL */}
                      <div className="flex justify-between items-center bg-green-900/10 -mx-2 px-2 py-1.5 sm:py-2 rounded">
                        <span className="text-xs sm:text-sm text-gray-400">{t('vaults:card.takaraAPY')}</span>
                        <span className="text-base sm:text-lg font-semibold text-green-400">
                          {vault.dynamicTakaraAPY || vault.baseTakaraAPY}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs sm:text-sm text-gray-400">{t('vaults:card.minAllocation')}</span>
                        <span className="text-xs sm:text-sm font-medium text-white">
                          ${vault.minInvestment.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Payout Schedule */}
                    <div className="mb-4 sm:mb-6 p-2 sm:p-3 bg-background-elevated rounded-lg">
                      <div className="text-[10px] sm:text-xs text-gray-400 mb-1">{t('vaults:card.payoutSchedule')}</div>
                      <div className="text-xs sm:text-sm font-medium text-white">
                        {vault.payoutSchedule.replace('_', ' ')}
                      </div>
                    </div>

                    {/* Tier Mining Status */}
                    {vault.currentTier && (
                    <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-background-elevated rounded-lg border border-gold-500/20">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs sm:text-sm text-gray-400">{t('vaults:card.miningStatus')}</span>
                        <span className={`text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${
                          vault.currentTier.status === 'ACTIVE_MINING'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {vault.currentTier.status === 'ACTIVE_MINING' ? `⛏️ ${t('vaults:card.active')}` : `📊 ${t('vaults:card.collecting')}`}
                        </span>
                      </div>
                      <div className="relative h-2 sm:h-3 bg-navy-900 rounded-full overflow-hidden mb-2">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min((Number(vault.currentTier.currentFilled || 0) / Number(vault.currentTier.miningThreshold || 25000)) * 100, 100)}%`,
                            background: vault.currentTier.status === 'ACTIVE_MINING'
                              ? 'linear-gradient(90deg, #EFCE92, #22c55e)'
                              : 'linear-gradient(90deg, #EFCE92, #D9A85A)'
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] sm:text-xs">
                        <span className="text-gray-500">
                          ${Number(vault.currentTier.currentFilled || 0).toLocaleString()}
                        </span>
                        <span className="text-gold-400 font-medium">
                          ${Number(vault.currentTier.miningThreshold || 25000).toLocaleString()}
                        </span>
                      </div>
                      {vault.currentTier.status !== 'ACTIVE_MINING' && (
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-1 sm:mt-2">
                          {t('vaults:card.miningStarts')}
                        </p>
                      )}
                    </div>
                    )}
                  </>
                )}

                {/* CTA */}
                {vault.isComingSoon || !isVaultPurchasingEnabled ? (
                  <button
                    disabled
                    className="w-full text-center block py-3 rounded-lg font-semibold bg-background-primary border border-orange-500/30 cursor-not-allowed"
                  >
                    {vault.isComingSoon ? (
                      <span className="text-gradient-orange">{vault.comingSoonText || t('vaults:comingSoon')}</span>
                    ) : launchDate ? (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs font-medium text-gradient-orange">{t('vaults:comingSoon')}</span>
                        <span className="text-sm font-bold text-gradient-orange">
                          {countdown.days > 0 && `${countdown.days}d `}
                          {String(countdown.hours).padStart(2, '0')}:
                          {String(countdown.minutes).padStart(2, '0')}:
                          {String(countdown.seconds).padStart(2, '0')}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gradient-orange">{t('vaults:comingSoon')}</span>
                    )}
                  </button>
                ) : (
                  <Link
                    to={`/app/vaults/${vault.id}`}
                    className="btn-gold w-full text-center block py-3 rounded-lg font-semibold"
                  >
                    {t('vaults:card.viewDetails')}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  )
}

/**
 * Vault Countdown Component
 * Displays remaining time until vault deactivation
 */
function VaultCountdown({ countdownSeconds }: { countdownSeconds: number }) {
  const [remaining, setRemaining] = useState(countdownSeconds)

  useEffect(() => {
    setRemaining(countdownSeconds)
    
    if (countdownSeconds <= 0) return

    const interval = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [countdownSeconds])

  const days = Math.floor(remaining / 86400)
  const hours = Math.floor((remaining % 86400) / 3600)
  const minutes = Math.floor((remaining % 3600) / 60)
  const seconds = remaining % 60

  if (remaining <= 0) {
    return <span className="text-[10px] sm:text-xs text-red-400 font-bold">CLOSED</span>
  }

  return (
    <span className="text-[10px] sm:text-xs text-orange-400 font-bold">
      {days > 0 && `${days}d `}
      {hours.toString().padStart(2, '0')}:
      {minutes.toString().padStart(2, '0')}:
      {seconds.toString().padStart(2, '0')}
    </span>
  )
}
